import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import PostCard from '../components/ui/PostCard';
import { posts, groups } from '../data/mockData';

const HomeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const FeedSection = styled.div``;

const SideSection = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const TrendingTopic = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const TopicIcon = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  margin-right: ${({ theme }) => theme.spacing.sm};
`;

const TopicInfo = styled.div`
  flex: 1;
`;

const TopicName = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TopicCount = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const GroupCard = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const GroupIcon = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  margin-right: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.cardBg};
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const GroupInfo = styled.div`
  flex: 1;
`;

const GroupName = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const GroupMembers = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const JoinButton = styled(Link)`
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    color: white;
  }
`;

const ViewAllLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  
  &:hover {
    text-decoration: underline;
  }
`;

// Trending topics data
const trendingTopics = [
  { id: 1, name: 'Summer Travel', icon: '🏝️', count: '2.5k posts' },
  { id: 2, name: 'React Development', icon: '⚛️', count: '1.8k posts' },
  { id: 3, name: 'Photography Tips', icon: '📸', count: '1.2k posts' },
  { id: 4, name: 'Gaming News', icon: '🎮', count: '3.1k posts' },
  { id: 5, name: 'Cooking Recipes', icon: '🍳', count: '950 posts' },
];

// Filter popular groups for suggestions
const suggestedGroups = groups.filter(group => group.isPopular).slice(0, 3);

const HomePage = () => {
  // Sort posts by recency (using timeAgo as a proxy)
  const sortedPosts = [...posts].sort((a, b) => {
    const timeA = a.timeAgo.includes('hour') ? parseInt(a.timeAgo) :
                 a.timeAgo.includes('day') ? parseInt(a.timeAgo) * 24 :
                 a.timeAgo.includes('week') ? parseInt(a.timeAgo) * 24 * 7 : 0;

    const timeB = b.timeAgo.includes('hour') ? parseInt(b.timeAgo) :
                 b.timeAgo.includes('day') ? parseInt(b.timeAgo) * 24 :
                 b.timeAgo.includes('week') ? parseInt(b.timeAgo) * 24 * 7 : 0;

    return timeA - timeB;
  });

  return (
    <HomeContainer>
      <FeedSection>
        <SectionTitle>Home Feed</SectionTitle>

        {sortedPosts.map((post, index) => (
          // Insert a group suggestion card after every 3 posts
          <React.Fragment key={post.id}>
            <PostCard post={post} />
            {index % 3 === 2 && index < sortedPosts.length - 1 && (
              <Card>
                <SectionTitle>Suggested Group</SectionTitle>
                <GroupCard>
                  <GroupIcon>{suggestedGroups[index % suggestedGroups.length].icon}</GroupIcon>
                  <GroupInfo>
                    <GroupName>{suggestedGroups[index % suggestedGroups.length].name}</GroupName>
                    <GroupMembers>{suggestedGroups[index % suggestedGroups.length].members.toLocaleString()} members</GroupMembers>
                  </GroupInfo>
                  <JoinButton to={`/group/${suggestedGroups[index % suggestedGroups.length].id}`}>
                    Join
                  </JoinButton>
                </GroupCard>
              </Card>
            )}
          </React.Fragment>
        ))}
      </FeedSection>

      <SideSection>
        <Card>
          <SectionTitle>Trending Topics</SectionTitle>
          {trendingTopics.map(topic => (
            <TrendingTopic key={topic.id}>
              <TopicIcon>{topic.icon}</TopicIcon>
              <TopicInfo>
                <TopicName>{topic.name}</TopicName>
                <TopicCount>{topic.count}</TopicCount>
              </TopicInfo>
            </TrendingTopic>
          ))}
          <ViewAllLink to="/trending">View All Trending Topics</ViewAllLink>
        </Card>

        <Card>
          <SectionTitle>Suggested Groups</SectionTitle>
          {suggestedGroups.map(group => (
            <GroupCard key={group.id}>
              <GroupIcon>{group.icon}</GroupIcon>
              <GroupInfo>
                <GroupName>{group.name}</GroupName>
                <GroupMembers>{group.members.toLocaleString()} members</GroupMembers>
              </GroupInfo>
              <JoinButton to={`/group/${group.id}`}>
                Join
              </JoinButton>
            </GroupCard>
          ))}
          <ViewAllLink to="/discover">Discover More Groups</ViewAllLink>
        </Card>
      </SideSection>
    </HomeContainer>
  );
};

export default HomePage;
